import { Input, Select, Textarea } from '@/components/ui/FormControls';
import { parseCalendarDate } from '@klothiq/domain';
// ─────────────────────────────────────────────
// KLOTHIQ — Task Detail Modal / Drawer
// ─────────────────────────────────────────────
// Provides comprehensive task management:
// status/progress transitions, blocker notes,
// reassignment with reason, checklist items,
// comments thread, and activity timeline.
// ─────────────────────────────────────────────

import { useState, useEffect, useRef } from 'react';
import {
  X,
  Calendar,
  AlertTriangle,
  ListChecks,
  MessageSquare,
  History,
  Send,
  UserCheck,
  ShieldAlert,
  Edit2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  useUpdateTaskProgressStatus,
  useReassignTask,
  useUpdateTask,
  useTaskComments,
  useAddTaskComment,
  useTaskActivity,
} from '@/hooks/useTasks';
import { useOrgProfiles } from '@/hooks/useOrgProfiles';
import {
  validateWorkProgress,
  workTransitionAllowed,
  normalizeWorkTask,
  type WorkTask,
  type WorkStatus,
} from '@klothiq/domain';
import { useWorkIntent } from '@/hooks/useWorkIntent';
import WorkModalShell from '@/components/common/WorkModalShell';
import TaskFormModal from '../plans/TaskFormModal';

interface TaskDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: WorkTask | null;
  onReload: () => Promise<WorkTask | undefined>;
  planId?: string;
}

export default function TaskDetailModal(props: TaskDetailModalProps) {
  if (!props.isOpen || !props.task) return null;
  return <OpenTaskDetailModal key={props.task.id} {...props} task={props.task} />;
}
function OpenTaskDetailModal({
  onClose,
  onReload,
  task,
  planId: explicitPlanId,
}: TaskDetailModalProps & { task: NonNullable<TaskDetailModalProps['task']> }) {
  const currentPlanId = explicitPlanId || task.plan_id;
  const { profile, can } = useAuth();
  const canManage = can('plans.manage');
  const isAssignee = profile?.id === task.assignee_id;
  const canUpdateStatus = canManage || (isAssignee && can('my_tasks.update'));
  const canComment = canManage || (isAssignee && can('my_tasks.comment'));

  const {
    data: orgProfiles = [],
    isError: assigneesError,
    isLoading: assigneesLoading,
    refetch: reloadAssignees,
  } = useOrgProfiles(true);
  const {
    data: comments = [],
    isLoading: commentsLoading,
    isError: commentsError,
    refetch: reloadComments,
  } = useTaskComments(task.id);
  const {
    data: activities = [],
    isLoading: activityLoading,
    isError: activityError,
    refetch: reloadActivity,
  } = useTaskActivity(task.id);

  const updateStatus = useUpdateTaskProgressStatus();
  const reassignTask = useReassignTask();
  const updateTask = useUpdateTask();
  const addComment = useAddTaskComment();

  // Status & Progress State
  const [selectedStatus, setSelectedStatus] = useState<WorkStatus>(task.status);
  const [progressVal, setProgressVal] = useState<number>(task.progress);
  const [blockerNote, setBlockerNote] = useState<string>(task.blocker_note || '');
  const [reopenNote, setReopenNote] = useState<string>('');
  const [statusError, setStatusError] = useState<string | null>(null);

  // Reassignment Form State
  const [isReassigning, setIsReassigning] = useState(false);
  const [newAssigneeId, setNewAssigneeId] = useState<string>(task.assignee_id || '');
  const [reassignReason, setReassignReason] = useState<string>('');
  const [reassignError, setReassignError] = useState<string | null>(null);

  // Comments State
  const [activeTab, setActiveTab] = useState<'details' | 'comments' | 'activity'>('details');
  const [newComment, setNewComment] = useState('');

  // Checklist State
  const [checklistItems, setChecklistItems] = useState(task.checklist || []);

  const [baseline, setBaseline] = useState(task);
  const [isEditing, setIsEditing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const intent = useWorkIntent();
  const writeLock = useRef(false);
  const isBusy =
    updateStatus.isPending ||
    reassignTask.isPending ||
    updateTask.isPending ||
    addComment.isPending ||
    reloading;
  const statusDirty =
    selectedStatus !== baseline.status ||
    progressVal !== baseline.progress ||
    blockerNote !== (baseline.blocker_note ?? '') ||
    !!reopenNote.trim();
  const assignmentDirty = newAssigneeId !== (baseline.assignee_id ?? '') || !!reassignReason.trim();
  const dirty = statusDirty || assignmentDirty || !!newComment.trim();
  const hasConflict = task.version !== baseline.version && !isBusy;
  const readOnly = task.plan_is_archived || hasConflict;
  const applySnapshot = (next: WorkTask, preserveStatus = false, preserveAssignment = false) => {
    next = normalizeWorkTask(next);
    setBaseline(next);
    setChecklistItems(next.checklist);
    if (!preserveStatus) {
      setSelectedStatus(next.status);
      setProgressVal(next.progress);
      setBlockerNote(next.blocker_note ?? '');
      setReopenNote('');
    }
    if (!preserveAssignment) {
      setNewAssigneeId(next.assignee_id ?? '');
      setReassignReason('');
      setIsReassigning(false);
    }
    setStatusError(null);
    setReassignError(null);
  };
  useEffect(() => {
    if (task.version !== baseline.version && !isBusy && !dirty && !isEditing) applySnapshot(task);
  }, [task, baseline.version, isBusy, dirty, isEditing]);
  const requestClose = () => {
    if (!isBusy && !isEditing && (!dirty || window.confirm('Discard your unsaved task changes?')))
      onClose();
  };
  const reload = async () => {
    if (
      isBusy ||
      (dirty &&
        !window.confirm(
          'Reload the latest task and discard unsaved status or assignment changes? Your comment draft will be kept.',
        ))
    )
      return;
    setReloading(true);
    try {
      const next = await onReload();
      if (!next) {
        onClose();
        return;
      }
      applySnapshot(next);
      intent.reset();
    } catch (error) {
      setStatusError(error instanceof Error ? error.message : 'Task could not be reloaded.');
    } finally {
      setReloading(false);
    }
  };
  const handleStatusChange = (next: WorkStatus) => {
    if (
      isBusy ||
      readOnly ||
      !canUpdateStatus ||
      !workTransitionAllowed(baseline.status, next, canManage)
    )
      return;
    setSelectedStatus(next);
    setStatusError(null);
    if (next === 'todo') setProgressVal(0);
    if (next === 'completed') setProgressVal(100);
  };
  const handleStatusSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (writeLock.current || isBusy || readOnly || !canUpdateStatus) return;
    const error = validateWorkProgress({
      currentStatus: baseline.status,
      status: selectedStatus,
      progress: progressVal,
      canManage,
      blockerNote,
      reopenNote,
    });
    if (error) {
      setStatusError(error);
      return;
    }
    const input = {
      task_id: task.id,
      plan_id: currentPlanId,
      expected_version: baseline.version,
      status: selectedStatus,
      progress: progressVal,
      blocker_note: selectedStatus === 'blocked' ? blockerNote.trim() : null,
      reopen_note:
        baseline.status !== selectedStatus && ['completed', 'cancelled'].includes(baseline.status)
          ? reopenNote.trim()
          : null,
    };
    writeLock.current = true;
    try {
      const result = await updateStatus.mutateAsync({
        ...input,
        request_key: intent.key('status', input),
      });
      if (result.task) applySnapshot(result.task, false, assignmentDirty);
      intent.complete('status');
    } catch (error) {
      setStatusError(error instanceof Error ? error.message : 'Task could not be saved.');
    } finally {
      writeLock.current = false;
    }
  };
  const handleReassignSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (writeLock.current || isBusy || readOnly || !canManage) return;
    if (
      baseline.assignee_id &&
      baseline.assignee_id !== (newAssigneeId || null) &&
      (reassignReason.trim().length < 3 || reassignReason.trim().length > 500)
    ) {
      setReassignError('Enter a reassignment reason of 3–500 characters.');
      return;
    }
    const input = {
      task_id: task.id,
      plan_id: currentPlanId,
      expected_version: baseline.version,
      new_assignee_id: newAssigneeId || null,
      reason: reassignReason.trim() || null,
    };
    writeLock.current = true;
    try {
      const result = await reassignTask.mutateAsync({
        ...input,
        request_key: intent.key('reassign', input),
      });
      if (result.task) applySnapshot(result.task, statusDirty, false);
      intent.complete('reassign');
    } catch (error) {
      setReassignError(error instanceof Error ? error.message : 'Assignment could not be saved.');
    } finally {
      writeLock.current = false;
    }
  };
  const handleToggleChecklist = async (index: number) => {
    if (writeLock.current || isBusy || readOnly || !canUpdateStatus || task.checklist_error) return;
    writeLock.current = true;
    const previous = checklistItems;
    const updated = previous.map((entry, i) =>
      i === index ? { ...entry, done: !entry.done } : entry,
    );
    setChecklistItems(updated);
    const input = {
      task_id: task.id,
      plan_id: currentPlanId,
      expected_version: baseline.version,
      checklist: updated,
    };
    try {
      const result = await updateTask.mutateAsync({
        ...input,
        request_key: intent.key('checklist', input),
      });
      if (result.task) applySnapshot(result.task, statusDirty, assignmentDirty);
      intent.complete('checklist');
    } catch (error) {
      setChecklistItems(previous);
      setStatusError(error instanceof Error ? error.message : 'Checklist could not be saved.');
    } finally {
      writeLock.current = false;
    }
  };
  const handlePostComment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (writeLock.current || isBusy || readOnly || !canComment || !newComment.trim()) return;
    writeLock.current = true;
    const input = {
      task_id: task.id,
      plan_id: currentPlanId,
      expected_version: baseline.version,
      comment_text: newComment.trim(),
    };
    try {
      const result = await addComment.mutateAsync({
        ...input,
        request_key: intent.key('comment', input),
      });
      if (result.task) applySnapshot(result.task, statusDirty, assignmentDirty);
      setNewComment('');
      intent.complete('comment');
    } catch (error) {
      setStatusError(error instanceof Error ? error.message : 'Comment could not be saved.');
    } finally {
      writeLock.current = false;
    }
  };

  const isReopeningTask =
    baseline.status !== selectedStatus && ['completed', 'cancelled'].includes(baseline.status);

  const priorityStyles =
    {
      urgent: 'bg-rose-500/15 text-rose-500 border-rose-500/30',
      high: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
      normal: 'bg-primary/15 text-primary border-primary/30',
      low: 'bg-blue-500/15 text-blue-500 border-blue-500/30',
    }[task.priority] || 'bg-muted text-muted-foreground border-border';

  return (
    <WorkModalShell open onClose={requestClose} busy={isBusy || isEditing} title={task.title} wide>
      {/* Header */}
      <div className="flex items-start justify-between border-b border-border p-5 shrink-0 bg-muted/20">
        <div className="min-w-0 flex-1 pr-4">
          {task.plan_title && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium mb-1">
              <span>Plan:</span>
              <span className="text-foreground font-semibold truncate">{task.plan_title}</span>
            </div>
          )}
          <h2 className="text-lg font-bold text-foreground leading-snug">{task.title}</h2>

          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span
              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${priorityStyles}`}
            >
              {task.priority}
            </span>

            {task.due_date && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs ${
                  task.is_overdue
                    ? 'bg-rose-500/15 text-rose-500 font-semibold'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                <Calendar className="h-3 w-3" />
                {parseCalendarDate(task.due_date)?.toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}
                {task.is_overdue && ' (Overdue)'}
              </span>
            )}

            <span className="text-xs text-muted-foreground">
              Progress: <strong className="text-foreground">{task.progress}%</strong>
            </span>
          </div>
        </div>

        <button
          onClick={requestClose}
          disabled={isBusy || isEditing}
          aria-label="Close task"
          className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors shrink-0"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      {task.plan_is_archived && (
        <p role="status" className="rounded-xl border border-border p-3 text-sm">
          Archived plan — task details, comments and assignment are read-only.
        </p>
      )}
      {hasConflict && (
        <div role="alert" className="rounded-xl border border-amber-500/30 p-3 text-sm">
          A newer version is available. Your unsaved draft is retained.{' '}
          <button type="button" className="min-h-11 underline" onClick={reload}>
            Reload latest task
          </button>
        </div>
      )}
      {statusError && (
        <p role="alert" className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
          {statusError}{' '}
          <button type="button" disabled={isBusy} className="min-h-11 underline" onClick={reload}>
            Reload latest task
          </button>
        </p>
      )}
      {canManage && (
        <button
          type="button"
          disabled={isBusy || readOnly}
          className="mt-2 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border px-4 text-sm font-semibold disabled:opacity-50"
          onClick={() => {
            if (dirty && !window.confirm('Discard unsaved changes and edit task details?')) return;
            applySnapshot(task);
            setNewComment('');
            setIsEditing(true);
          }}
        >
          <Edit2 className="h-4 w-4" />
          Edit task details
        </button>
      )}

      {/* Tab Navigation */}
      <div className="flex flex-wrap border-b border-border px-1 bg-card shrink-0">
        <button
          onClick={() => setActiveTab('details')}
          className={`min-h-11 flex items-center gap-1.5 border-b-2 py-2.5 text-xs font-semibold transition-all mr-6 ${
            activeTab === 'details'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <ListChecks className="h-3.5 w-3.5" /> Details & Status
        </button>
        <button
          onClick={() => setActiveTab('comments')}
          className={`min-h-11 flex items-center gap-1.5 border-b-2 py-2.5 text-xs font-semibold transition-all mr-6 ${
            activeTab === 'comments'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <MessageSquare className="h-3.5 w-3.5" /> Comments ({comments.length})
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`min-h-11 flex items-center gap-1.5 border-b-2 py-2.5 text-xs font-semibold transition-all ${
            activeTab === 'activity'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <History className="h-3.5 w-3.5" /> Activity Log
        </button>
      </div>

      {/* Modal Body */}
      <div className="min-h-0 flex-1 overflow-y-auto py-4 space-y-5">
        {activeTab === 'details' && (
          <>
            {/* Description */}
            {task.description && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground mb-1.5">Description</h4>
                <div className="rounded-xl bg-muted/40 p-3 text-xs leading-relaxed text-foreground whitespace-pre-wrap">
                  {task.description}
                </div>
              </div>
            )}

            {/* Assignee Card with Reassign Action */}
            <div className="rounded-xl border border-border p-4 bg-muted/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-primary text-xs font-bold">
                    {task.assignee_name ? task.assignee_name.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Assignee</p>
                    <p className="text-sm font-semibold text-foreground">
                      {task.assignee_name || 'Unassigned'}
                      {task.assignee_role && (
                        <span className="ml-1.5 text-[10px] font-normal text-muted-foreground capitalize">
                          ({task.assignee_role})
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {canManage && (
                  <button
                    type="button"
                    disabled={isBusy || readOnly}
                    onClick={() => setIsReassigning((prev) => !prev)}
                    className="min-h-11 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted transition-colors flex items-center gap-1.5"
                  >
                    <UserCheck className="h-3.5 w-3.5" />
                    <span>{isReassigning ? 'Cancel' : 'Reassign'}</span>
                  </button>
                )}
              </div>

              {/* Inline Reassignment Form */}
              {isReassigning && (
                <form
                  onSubmit={handleReassignSubmit}
                  className="mt-4 pt-4 border-t border-border space-y-3"
                >
                  {reassignError && (
                    <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-2.5 text-xs text-destructive">
                      {reassignError}
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor="task-reassign-person"
                      className="block text-xs font-semibold text-foreground mb-1"
                    >
                      Select New Assignee
                    </label>
                    <Select
                      id="task-reassign-person"
                      value={newAssigneeId}
                      disabled={isBusy || readOnly || assigneesLoading || assigneesError}
                      onChange={(e) => setNewAssigneeId(e.target.value)}
                      className="min-h-11 w-full rounded-lg border border-input bg-background px-3 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      helpLabel="Select New Assignee"
                    >
                      <option value="">Unassign</option>
                      {orgProfiles.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.full_name} ({p.role || 'Staff'})
                        </option>
                      ))}
                    </Select>
                  </div>

                  {assigneesError && (
                    <p role="alert" className="text-sm text-destructive">
                      Team members could not be loaded.{' '}
                      <button
                        type="button"
                        onClick={() => reloadAssignees()}
                        className="min-h-11 underline"
                      >
                        Retry
                      </button>
                    </p>
                  )}
                  {task.assignee_id && (
                    <div>
                      <label
                        htmlFor="task-reassign-reason"
                        className="block text-xs font-semibold text-foreground mb-1"
                      >
                        Reason for Reassignment <span className="text-destructive">*</span>
                      </label>
                      <Input
                        id="task-reassign-reason"
                        disabled={isBusy || readOnly}
                        type="text"
                        value={reassignReason}
                        onChange={(e) => setReassignReason(e.target.value)}
                        placeholder="e.g. Workload rebalancing, leave of initial assignee"
                        maxLength={500}
                        required
                        className="min-h-11 w-full rounded-lg border border-input bg-background px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        helpLabel="Reason for Reassignment *"
                      />
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => setIsReassigning(false)}
                      className="min-h-11 rounded-lg px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isBusy || readOnly}
                      className="min-h-11 rounded-lg bg-primary px-3.5 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                      {reassignTask.isPending ? 'Updating...' : 'Confirm Reassign'}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Status & Progress Controller */}
            <div className="obsidian-panel rounded-xl border border-border p-4 bg-card">
              <h4 className="text-xs font-bold text-foreground uppercase tracking-wider mb-3">
                Status & Delivery Progress
              </h4>

              <form onSubmit={handleStatusSubmit} className="space-y-4">
                {/* Status Options */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {[
                    { id: 'todo', label: 'To Do', color: 'border-border' },
                    { id: 'in_progress', label: 'In Progress', color: 'border-primary' },
                    { id: 'blocked', label: 'Blocked', color: 'border-amber-500' },
                    { id: 'completed', label: 'Completed', color: 'border-emerald-500' },
                    { id: 'cancelled', label: 'Cancelled', color: 'border-zinc-500' },
                  ].map((s) => {
                    const isSelected = selectedStatus === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        aria-pressed={isSelected}
                        disabled={
                          !canUpdateStatus ||
                          isBusy ||
                          readOnly ||
                          !workTransitionAllowed(baseline.status, s.id as WorkStatus, canManage)
                        }
                        onClick={() => handleStatusChange(s.id as WorkStatus)}
                        className={`min-h-11 rounded-lg py-2 text-xs font-semibold border transition-all ${
                          isSelected
                            ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                            : 'bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground'
                        } disabled:opacity-50`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>

                {/* Blocker Note Field (Required if blocked) */}
                {selectedStatus === 'blocked' && (
                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 animate-in fade-in duration-150">
                    <div className="flex items-center gap-1.5 text-amber-500 text-xs font-semibold mb-1">
                      <AlertTriangle className="h-4 w-4" />
                      <span>Blocker Note Required (5-1000 characters)</span>
                    </div>
                    <Textarea
                      aria-label="Blocker note"
                      disabled={isBusy || readOnly || !canUpdateStatus}
                      value={blockerNote}
                      onChange={(e) => setBlockerNote(e.target.value)}
                      placeholder="Explain what is blocking progress, required dependencies, or external blockers..."
                      rows={2}
                      maxLength={1000}
                      required
                      className="w-full rounded-lg border border-amber-500/30 bg-background px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                    />
                  </div>
                )}

                {/* Reopen Note Field (Required if reopening completed or cancelled task) */}
                {isReopeningTask && (
                  <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 animate-in fade-in duration-150">
                    <div className="flex items-center gap-1.5 text-primary text-xs font-semibold mb-1">
                      <ShieldAlert className="h-4 w-4" />
                      <span>Reopen Note (Explain why this task is being reopened)</span>
                    </div>
                    <Input
                      aria-label="Reopening reason"
                      disabled={isBusy || readOnly || !canUpdateStatus}
                      type="text"
                      value={reopenNote}
                      onChange={(e) => setReopenNote(e.target.value)}
                      placeholder="e.g. Quality review identified missing samples"
                      maxLength={500}
                      required
                      className="w-full rounded-lg border border-primary/30 bg-background px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                )}

                {/* Progress Slider */}
                <div>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="font-semibold text-foreground">Completion Progress</span>
                    <span className="font-bold text-primary text-sm">
                      {Number.isFinite(progressVal) ? `${progressVal}%` : 'Enter progress'}
                    </span>
                  </div>
                  <label className="mb-3 block text-sm">
                    Progress (%)
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={selectedStatus === 'in_progress' ? 1 : 0}
                      max={['completed', 'cancelled'].includes(selectedStatus) ? 100 : 99}
                      step={1}
                      value={Number.isFinite(progressVal) ? progressVal : ''}
                      disabled={
                        !canUpdateStatus ||
                        isBusy ||
                        readOnly ||
                        ['todo', 'completed', 'cancelled'].includes(selectedStatus)
                      }
                      onChange={(event) =>
                        setProgressVal(event.target.value === '' ? Number.NaN : Number(event.target.value))
                      }
                      className="ml-3 min-h-11 w-24 rounded-lg border border-input bg-background px-3 text-base"
                      helpLabel="Progress (%)"
                    />
                  </label>
                  {selectedStatus === 'in_progress' && (
                    <p className="mb-2 text-xs text-muted-foreground">
                      Choose a whole percentage from 1 to 99. Use Completed for 100%.
                    </p>
                  )}
                  <Input
                    aria-label="Task progress percentage"
                    type="range"
                    min={selectedStatus === 'in_progress' ? 1 : 0}
                    max={['completed', 'cancelled'].includes(selectedStatus) ? 100 : 99}
                    step={1}
                    value={Number.isFinite(progressVal) ? progressVal : 0}
                    disabled={
                      !canUpdateStatus ||
                      isBusy ||
                      readOnly ||
                      selectedStatus === 'cancelled' ||
                      selectedStatus === 'completed' ||
                      selectedStatus === 'todo'
                    }
                    onChange={(e) => setProgressVal(Number(e.target.value))}
                    className="w-full accent-primary h-2 bg-muted rounded-lg cursor-pointer disabled:opacity-50"
                  />
                </div>

                {canUpdateStatus && (
                  <div className="flex justify-end pt-2">
                    <button
                      type="submit"
                      disabled={isBusy || readOnly}
                      className="min-h-11 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground shadow hover:bg-primary/90 transition-all disabled:opacity-50"
                    >
                      {updateStatus.isPending ? 'Updating...' : 'Save Status & Progress'}
                    </button>
                  </div>
                )}
              </form>
            </div>

            {task.checklist_error && (
              <p role="alert" className="text-sm text-destructive">
                {task.checklist_error}
              </p>
            )}
            {/* Checklist */}
            {checklistItems.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                  <ListChecks className="h-3.5 w-3.5 text-primary" /> Checklist (
                  {checklistItems.filter((c) => c.done).length}/{checklistItems.length} completed)
                </h4>
                <div className="space-y-1.5">
                  {checklistItems.map((item, idx) => (
                    <label
                      key={item.id || idx}
                      className="min-h-11 flex items-center gap-2.5 rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs cursor-pointer hover:bg-muted/40 transition-colors"
                    >
                      <Input
                        type="checkbox"
                        checked={item.done}
                        disabled={!canUpdateStatus || isBusy || readOnly || !!task.checklist_error}
                        onChange={() => handleToggleChecklist(idx)}
                        className="rounded border-input text-primary focus:ring-primary h-4 w-4"
                        helpLabel="Checklist step completion"
                      />
                      <span
                        className={`flex-1 text-foreground ${
                          item.done ? 'line-through text-muted-foreground' : ''
                        }`}
                      >
                        {item.text}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Comments Tab */}
        {activeTab === 'comments' && (
          <div className="space-y-4">
            <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
              {commentsError ? (
                <p role="alert" className="text-sm text-destructive">
                  Comments could not be loaded.{' '}
                  <button
                    type="button"
                    onClick={() => reloadComments()}
                    className="min-h-11 underline"
                  >
                    Retry
                  </button>
                </p>
              ) : commentsLoading ? (
                <div className="text-center py-6 text-xs text-muted-foreground">
                  Loading comments...
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-8 text-xs text-muted-foreground">
                  No comments yet. Start the conversation below.
                </div>
              ) : (
                comments.map((c) => (
                  <div
                    key={c.id}
                    className="rounded-xl border border-border bg-muted/20 p-3.5 space-y-1"
                  >
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-foreground">
                        {c.author_name}
                        {c.author_role && (
                          <span className="ml-1 text-[10px] text-muted-foreground font-normal capitalize">
                            ({c.author_role})
                          </span>
                        )}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(c.created_at).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <p className="text-xs text-foreground/90 whitespace-pre-wrap leading-relaxed">
                      {c.comment_text}
                    </p>
                  </div>
                ))
              )}
            </div>

            {/* Add Comment Input */}
            <form onSubmit={handlePostComment} className="flex gap-2 pt-2 border-t border-border">
              <Input
                aria-label="Task comment"
                type="text"
                value={newComment}
                disabled={!canComment || isBusy || readOnly}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a comment..."
                maxLength={2000}
                className="min-h-11 min-w-0 flex-1 rounded-xl border border-input bg-background px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="submit"
                disabled={!canComment || !newComment.trim() || isBusy || readOnly}
                className="rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center gap-1.5"
              >
                <Send className="h-3.5 w-3.5" />
                <span>Send</span>
              </button>
            </form>
          </div>
        )}

        {/* Activity Log Tab */}
        {activeTab === 'activity' && (
          <div className="space-y-3">
            {activityError ? (
              <p role="alert" className="text-sm text-destructive">
                Activity could not be loaded.{' '}
                <button
                  type="button"
                  onClick={() => reloadActivity()}
                  className="min-h-11 underline"
                >
                  Retry
                </button>
              </p>
            ) : activityLoading ? (
              <div className="text-center py-6 text-xs text-muted-foreground">
                Loading activity...
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-8 text-xs text-muted-foreground">
                No activity recorded.
              </div>
            ) : (
              activities.map((a) => (
                <div
                  key={a.id}
                  className="flex items-start gap-2.5 text-xs border-l-2 border-border pl-3 py-1"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">{a.actor_name}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(a.created_at).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <p className="text-muted-foreground capitalize mt-0.5">
                      {a.action.replace(/_/g, ' ')}
                    </p>
                    {a.reason && (
                      <p className="mt-1 rounded-lg bg-muted/40 p-1.5 text-[11px] text-foreground italic">
                        Note: "{a.reason}"
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      <TaskFormModal
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        taskToEdit={task}
        planId={currentPlanId!}
        planTargetDate={task.plan_target_date}
        planArchived={task.plan_is_archived}
      />
    </WorkModalShell>
  );
}
